import { Stack, Text } from "@certn/cascadia";
import { Card, Icon } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useCanViewClientPortalPricing } from "../permissions/Permissions";
import { TagList } from "../tag-list/TagList";
import { formatNullableCurrency } from "../../utils/formatNullableCurrency";

interface Props {
  title: string;
  checks: string[];
  description: string;
  tags?: string[];
  price?: string;
  onPress: () => void;
  testID: string;
  isDisabled: boolean;
}

export function PackageCardLegacy({
  title,
  checks,
  description,
  tags = [],
  price,
  onPress,
  testID,
  isDisabled = false,
}: Props) {
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const CAPACITY = 3;
  const packagePrice = formatNullableCurrency(price, {
    currency: "CAD",
    locale: "en-CA",
    fallback: null,
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onPress();
    }
  };

  return (
    <Card className={styles.card}>
      <div
        role="button"
        aria-disabled={isDisabled}
        tabIndex={0}
        onClick={isDisabled ? () => {} : onPress}
        onKeyDown={isDisabled ? () => {} : handleKeyDown}
        className={styles.button}
        data-testid={`${testID}-package-button`}
      >
        <Stack gap="space-2">
          <div className={styles.titleSection}>
            <div className={styles.title}>
              <Icon name="package_2" color="icon-accent-tofino" />
              <Text
                style="global/heading-medium"
                testID={`${testID}-package-name`}
              >
                {title}
              </Text>
            </div>
            {isDisabled && (
              <Icon name="lock" color="icon-weak" testID="lock-icon" />
            )}
          </div>
          <div>
            <Text
              style="global/info-label"
              color="text-weak"
              testID={`${testID}-package-checks`}
            >
              {checks.slice(0, CAPACITY).join(", ")}
            </Text>
            {checks.length > CAPACITY && (
              <Text
                style="global/info-label"
                color="text-weak"
                testID={`${testID}-package-checks-overflow`}
              >
                {` +${checks.length - CAPACITY}`}
              </Text>
            )}
          </div>
          <Text
            style="global/info-label"
            color="text-weak"
            className={
              canViewClientPortalPricing && packagePrice && tags.length
                ? css(styles.description, css({ WebkitLineClamp: "1" }))
                : styles.description
            }
            testID={`${testID}-package-description`}
          >
            {description}
          </Text>
        </Stack>
        <Stack gap="space-2">
          <div style={{ paddingTop: "var(--space-2)" }}>
            <TagList
              tags={tags}
              capacity={CAPACITY}
              testID={`${testID}-package`}
            />
          </div>
          {canViewClientPortalPricing && packagePrice && (
            <Text
              style="global/heading-small"
              color="text-accent-tofino-strong"
              testID={`${testID}-package-price`}
            >
              {t("From {{packagePrice}}", { packagePrice })}
            </Text>
          )}
        </Stack>
      </div>
    </Card>
  );
}

const styles = {
  card: css({
    display: "flex",
    height: 160,
    width: 394,
    minWidth: 260,
    maxWidth: 488,
    borderRadius: "var(--border-radius-3)",
    "&:hover": {
      boxShadow: "var(--shadow-card)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
    cursor: "pointer",
    padding: "var(--space-05)",
  }),
  button: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "var(--space-3) var(--space-4) var(--space-4)",
    width: "calc(100% - 2rem)",
  }),
  titleSection: css({
    display: "flex",
    justifyContent: "space-between",
  }),
  title: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  description: css({
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: "2",
    overflow: "hidden",
    textOverflow: "ellipsis",
    overflowWrap: "break-word",
  }),
};
